import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import axiosInstance from "../../../utils/config/config";
import { BASE_URL } from "../../../utils/config/config";
import { compressImage } from "../../../utils/compressImage";
import {
  MULTILINE_ARRAY_FIELDS,
  SINGLE_DOC_FIELDS,
  MULTI_DOC_FIELDS,
  isValidUrl,
} from "./MyProfile.constants";

const EMPTY = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  gender: "",
  timeZone: "",
  bio: "",
  specialities: [],
  experience: [],
  education: [],
  certifications: [],
  hourlyRate: "",
  licensed: false,
  languages: [],
  instagram: "",
  fb: "",
  linkedIn: "",
  website: "",
  simplePracticeBookingUrl: "",
  practitionerStatus: false,
  avatar: "",
  coverPicture: "",
  // doc fields (single string / array) are seeded from the constants below
};

// DB stores experience/education/certifications as arrays; the form edits them
// as one-entry-per-line text. Convert on load/save.
const arrToText = (v) => (Array.isArray(v) ? v.join("\n") : v || "");
const textToArr = (v) =>
  String(v || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

const seedDocs = () => {
  const d = {};
  SINGLE_DOC_FIELDS.forEach((k) => (d[k] = ""));
  MULTI_DOC_FIELDS.forEach((k) => (d[k] = []));
  return d;
};

export const useMyProfile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // per-field upload spinners: { avatar: true, scopeOfPractice: true, ... }
  const [uploading, setUploading] = useState({});
  const [form, setForm] = useState({ ...EMPTY, ...seedDocs() });
  // Unpublished submission awaiting admin review: { status, reviewComment, updatedAt } | null
  const [pendingEdit, setPendingEdit] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  const notify = useCallback((severity, msg) => setSnack({ open: true, severity, msg }), []);

  const setField = useCallback((key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("practitioners/me");
      const next = { ...EMPTY, ...seedDocs(), ...data };
      MULTILINE_ARRAY_FIELDS.forEach((f) => (next[f] = arrToText(data[f])));
      setForm(next);
      setPendingEdit(data.pendingEdit || null);
    } catch (err) {
      notify("error", err.response?.data?.message || "Failed to load your profile");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  // Upload one file to S3 → returns its URL (or null on failure).
  const uploadToS3 = useCallback(async (file) => {
    const fd = new FormData();
    fd.append("file", await compressImage(file)); // shrink big photos → avoid 413
    const res = await axios.post(`${BASE_URL}aws/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data?.fileUrl || null;
  }, []);

  // Single-file field (avatar, coverPicture, single docs).
  const handleSingleUpload = useCallback(
    async (fieldKey, file) => {
      if (!file) return;
      setUploading((u) => ({ ...u, [fieldKey]: true }));
      try {
        const url = await uploadToS3(file);
        if (url) setField(fieldKey, url);
      } catch (err) {
        notify("error", err.response?.data?.message || "Upload failed");
      } finally {
        setUploading((u) => ({ ...u, [fieldKey]: false }));
      }
    },
    [uploadToS3, setField, notify]
  );

  // Multi-file field (arrays): append every selected file's URL.
  const handleMultiUpload = useCallback(
    async (fieldKey, fileList) => {
      const files = Array.from(fileList || []);
      if (!files.length) return;
      setUploading((u) => ({ ...u, [fieldKey]: true }));
      try {
        const urls = [];
        for (const f of files) {
          const url = await uploadToS3(f);
          if (url) urls.push(url);
        }
        setForm((f) => ({ ...f, [fieldKey]: [...(f[fieldKey] || []), ...urls] }));
      } catch (err) {
        notify("error", err.response?.data?.message || "Upload failed");
      } finally {
        setUploading((u) => ({ ...u, [fieldKey]: false }));
      }
    },
    [uploadToS3, notify]
  );

  // Remove a file. index undefined => clear a single field; index set => splice an array.
  const handleRemoveFile = useCallback((fieldKey, index) => {
    setForm((f) => {
      if (index === undefined) return { ...f, [fieldKey]: "" };
      const arr = Array.isArray(f[fieldKey]) ? f[fieldKey].slice() : [];
      arr.splice(index, 1);
      return { ...f, [fieldKey]: arr };
    });
  }, []);

  const validate = useCallback(() => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      return "First and last name are required.";
    }
    if (form.hourlyRate !== "" && Number(form.hourlyRate) < 0) {
      return "Hourly rate cannot be negative.";
    }
    const urlFields = [
      ["Instagram", form.instagram],
      ["Facebook", form.fb],
      ["LinkedIn", form.linkedIn],
      ["Website", form.website],
      ["SimplePractice link", form.simplePracticeBookingUrl],
    ];
    for (const [label, val] of urlFields) {
      if (!isValidUrl(val)) return `${label} must be a valid URL.`;
    }
    return null;
  }, [form]);

  const handleSubmit = useCallback(async () => {
    const err = validate();
    if (err) {
      notify("error", err);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone,
        gender: form.gender,
        timeZone: form.timeZone,
        bio: form.bio,
        specialities: form.specialities,
        languages: form.languages,
        hourlyRate: form.hourlyRate === "" ? null : Number(form.hourlyRate),
        licensed: !!form.licensed,
        instagram: form.instagram,
        fb: form.fb,
        linkedIn: form.linkedIn,
        website: form.website,
        simplePracticeBookingUrl: form.simplePracticeBookingUrl,
        avatar: form.avatar,
        avatarPosition: form.avatarPosition,
        coverPicture: form.coverPicture,
      };
      MULTILINE_ARRAY_FIELDS.forEach((f) => (payload[f] = textToArr(form[f])));
      SINGLE_DOC_FIELDS.forEach((k) => (payload[k] = form[k] || ""));
      MULTI_DOC_FIELDS.forEach((k) => (payload[k] = form[k] || []));

      const { data } = await axiosInstance.put("practitioners/me", payload);
      notify(
        "success",
        data?.pendingReview === false
          ? "No changes to submit — your profile already matches this."
          : "Changes submitted — an admin will review and publish them."
      );
      await load(); // refresh the pending-review banner + overlaid values
    } catch (err2) {
      notify("error", err2.response?.data?.message || "Failed to submit changes");
    } finally {
      setSaving(false);
    }
  }, [form, validate, notify, load]);

  const closeSnack = useCallback(() => setSnack((s) => ({ ...s, open: false })), []);

  return {
    loading,
    saving,
    uploading,
    form,
    pendingEdit,
    setField,
    handleSingleUpload,
    handleMultiUpload,
    handleRemoveFile,
    handleSubmit,
    snack,
    closeSnack,
  };
};
